import {Controller} from "@hotwired/stimulus"
import {placeFloatingPanel} from "../../lib/floating_panel"

// Popover for redesigned (ice-) screens.
//
//   %span{ data: { controller: 'ice-popover',
//                  'ice-popover-title-value': 'Packaging',
//                  'ice-popover-content-value': 'Box of 12',
//                  'ice-popover-placement-value': 'bottom-left auto',
//                  'ice-popover-trigger-value': 'click',
//                  'ice-popover-boundary-value': 'container' } }
//     = icon(:box_open)
//
//   title      optional heading
//   placement  see lib/floating_panel (default 'bottom auto')
//   trigger    'hover' (default) or 'click'
//   boundary   what the panel has to stay inside: 'viewport' (default) or 'container'
//
// The `popover` controller hands off to Bootstrap 3's jQuery plugin, which brings its
// own look and cannot be styled to match. This renders its own panel instead.
export default class extends Controller {
    static values = {
        title: String,
        content: String,
        contentId: String,
        placement: {type: String, default: "bottom auto"},
        trigger: {type: String, default: "hover"},
        boundary: {type: String, default: "viewport"},
        size: {type: String, default: "default"}
    }

    connect() {
        this.panel = null
        this.hideTimer = null
        this.pointerInside = {trigger: false, panel: false}

        this.onEnter = this.onEnter.bind(this)
        this.onLeave = this.onLeave.bind(this)
        this.onToggle = this.onToggle.bind(this)
        this.onActivate = this.onActivate.bind(this)
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this)
        this.onKeydown = this.onKeydown.bind(this)
        this.reposition = this.reposition.bind(this)

        if (this.byClick) {
            this.element.addEventListener("click", this.onToggle)
            this.element.addEventListener("keydown", this.onActivate)
        } else {
            this.element.addEventListener("mouseenter", this.onEnter)
            this.element.addEventListener("mouseleave", this.onLeave)
            this.element.addEventListener("focus", this.onEnter)
            this.element.addEventListener("blur", this.onLeave)
        }

        if (!this.element.hasAttribute("tabindex") && !this.element.matches("a, button, input, select, textarea")) {
            this.element.setAttribute("tabindex", "0")
        }
    }

    disconnect() {
        this.element.removeEventListener("click", this.onToggle)
        this.element.removeEventListener("keydown", this.onActivate)
        this.element.removeEventListener("mouseenter", this.onEnter)
        this.element.removeEventListener("mouseleave", this.onLeave)
        this.element.removeEventListener("focus", this.onEnter)
        this.element.removeEventListener("blur", this.onLeave)
        this.hide()
    }

    get byClick() {
        return this.triggerValue === "click"
    }

    // 'container' means the nearest positioned ancestor is both where the panel is
    // mounted and what `auto` measures against; 'viewport' means <body> and the window.
    get inContainer() {
        return this.boundaryValue === "container"
    }

    onEnter() {
        this.pointerInside.trigger = true
        this.cancelHide()
        this.show()
    }

    onLeave() {
        this.pointerInside.trigger = false
        this.scheduleHide()
    }

    onToggle(event) {
        event.preventDefault()
        event.stopPropagation()
        this.panel ? this.hide() : this.show()
    }

    onActivate(event) {
        if (event.key !== "Enter" && event.key !== " ") return

        this.onToggle(event)
    }

    // Click mode closes on a click anywhere else. pointerdown rather than click so it
    // fires before the target's own handler runs.
    onDocumentPointerDown(event) {
        if (this.element.contains(event.target)) return
        if (this.panel && this.panel.contains(event.target)) return

        this.hide()
    }

    onKeydown(event) {
        if (event.key === "Escape") this.hide()
    }

    // Hover keeps the panel open while the pointer is over the panel itself, which is
    // what lets you read a long note or reach a link inside it. The grace period covers
    // the gap between anchor and panel.
    scheduleHide() {
        if (this.byClick) return

        this.cancelHide()
        this.hideTimer = window.setTimeout(() => {
            if (!this.pointerInside.trigger && !this.pointerInside.panel) this.hide()
        }, 120)
    }

    cancelHide() {
        if (this.hideTimer) window.clearTimeout(this.hideTimer)
        this.hideTimer = null
    }

    show() {
        if (this.panel) return
        if (!this.contentValue && !this.titleValue && !this.contentIdValue) return

        this.panel = this.buildPanel()

        if (this.inContainer) {
            // Mounted in the nearest already-positioned ancestor and offset from the
            // trigger's place inside it. Nothing on the caller's element is touched —
            // an earlier version forced ice:relative onto the trigger, which both
            // mutated someone else's markup and made an inline <span> a containing
            // block. Still clipped by an ancestor's overflow: hidden, which is the
            // trade for staying in the flow, and why body is the default.
            this.container = this.positionedAncestor()
            this.container.appendChild(this.panel)
            this.reposition()
        } else {
            document.body.appendChild(this.panel)
            this.reposition()
            // Scrolling or resizing moves the anchor but not a fixed panel.
            window.addEventListener("scroll", this.reposition, true)
            window.addEventListener("resize", this.reposition)
        }

        if (!this.byClick) {
            this.panel.addEventListener("mouseenter", () => {
                this.pointerInside.panel = true
                this.cancelHide()
            })
            this.panel.addEventListener("mouseleave", () => {
                this.pointerInside.panel = false
                this.scheduleHide()
            })
        }

        if (this.byClick) document.addEventListener("pointerdown", this.onDocumentPointerDown)
        document.addEventListener("keydown", this.onKeydown)
    }

    hide() {
        this.cancelHide()
        if (!this.panel) return

        window.removeEventListener("scroll", this.reposition, true)
        window.removeEventListener("resize", this.reposition)
        document.removeEventListener("pointerdown", this.onDocumentPointerDown)
        document.removeEventListener("keydown", this.onKeydown)

        this.panel.remove()
        this.panel = null
        this.container = null
        this.pointerInside.panel = false
    }

    // Two elements, not one. The rescale rules are descendant selectors
    // (`ice:rem16-scale .ice:text-xs`), while the scope root itself is a compound
    // selector that sets font-size to 16px. Putting both on one element means the
    // utilities never match and everything inherits the scope's 16px — which is how
    // this popover first shipped with text twice the intended size.
    buildPanel() {
        const root = document.createElement("div")

                // 1060 sits under the navbar by design, which puts it under a modal (1101)
                // and its backdrop (1100). The panel mounts on <body>, not in the dialog.
        const layer = this.element.closest(".modal") ? "ice:z-popover-modal" : "ice:z-popover"

        root.className = [
            "ice:preflight ice:rem16-scale",
            this.inContainer ? "ice:absolute" : "ice:fixed",
            layer
        ].join(" ")
        root.setAttribute("role", "tooltip")

        const card = document.createElement("div")
        card.className = [
            "ice:w-max ice:rounded-ui ice:border ice:border-slate-200 ice:bg-white",
            this.sizeValue === "wide" ? "ice:max-w-[440px]" : "ice:max-w-[280px]",
            "ice:px-3 ice:py-2 ice:text-xs ice:leading-normal ice:text-slate-700 ice:shadow-popover"
        ].join(" ")

        // textContent per node rather than innerHTML: the values are record data
        // (order numbers, product names), not markup we control.
        if (this.titleValue) {
            const heading = document.createElement("div")
            heading.className = "ice:mb-1 ice:font-semibold ice:text-slate-900"
            heading.textContent = this.titleValue
            card.appendChild(heading)
        }

        if (this.contentIdValue) {
            const source = document.getElementById(this.contentIdValue)

            if (source) {
                card.appendChild(source.content ? source.content.cloneNode(true) : source.cloneNode(true))
            }
        } else if (this.contentValue) {
            // One line per newline in the value. A popover that lists facts — phone,
            // email, who deleted it and when — reads as a paragraph when they are
            // joined, and record data cannot carry its own markup to break itself up.
            this.contentValue.split("\n").forEach((line) => {
                const body = document.createElement("div")
                body.textContent = line
                card.appendChild(body)
            })
        }

        root.appendChild(card)
        return root
    }

    // One geometry path for both mounts. The anchor is always the trigger; `boundary`
    // is what changes — the viewport for a body mount, the container for an inline one.
    //
    // Writing viewport coordinates works for the absolute case too: placeFloatingPanel
    // measures where the panel actually landed and corrects by the difference, so it
    // does not care what the containing block turned out to be.
    reposition() {
        if (!this.panel) return

        placeFloatingPanel(this.panel, this.element.getBoundingClientRect(), {
            placement: this.placementValue,
            boundary: this.inContainer ? this.container : null
        })
    }

    // The element an absolutely positioned child would actually resolve against, so
    // the offsets below mean what they say. Walks up for a non-static `position`;
    // offsetParent would answer this too but returns null for a hidden subtree and
    // skips over `position: fixed`. Falls back to the parent, which at least keeps the
    // panel next to its trigger in the DOM.
    positionedAncestor() {
        let node = this.element.parentElement

        while (node && node !== document.body) {
            if (getComputedStyle(node).position !== "static") return node
            node = node.parentElement
        }

        return this.element.parentElement || document.body
    }

}
